import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Input,
  InputLabel,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from "@mui/material";
import {
  GoogleMap,
  MarkerF,
  StandaloneSearchBox,
} from "@react-google-maps/api";
import moment from "moment";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CameraIcon,
  CameraIconGrey,
  CloseIcon,
  UploadIcon,
} from "../../../assets/icons/icons";
import { useSessionStorage } from "../../../hooks/useSessionStorage";
import { formatDateTime } from "../../../lib/time";
import { useGetProjectImagesExifData, useUploadProjectImages } from "./hooks";
import SelectTagsComponent from "./SelectTagsComponent";
import { useAuth0 } from "@auth0/auth0-react";
import { useGetInfo } from "../../dashboard/hooks";
import { WebcamComponent } from "../../video/Webcam";
const types = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp', 
  'image/tiff', 
  'image/svg+xml',
  'video/mp4',
  'video/mpeg',
  'video/quicktime',
  'video/webm',
  'video/x-msvideo', 
  'video/x-flv', 
  'video/3gpp', 
  'video/ogg', 
  'video/x-matroska',
]

export const ProjectsUploader = ({}: any) => {
  const { id } = useParams();
  const locationFieldRef: any = useRef();
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [fileUploaded, setFileUploaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sessionUser] = useSessionStorage("user", {});
  const [authUser, setAuthUser] = useState<any | undefined>(null);
  const { isAuthenticated, user } = useAuth0();
  const [userRoles, setUserRoles] = useState<any[]>([]);
  const { data: userData, isFetching: isFetchingUserData } = useGetInfo(
    "profile",
    authUser ? authUser.sub : ""
  );
  const { mutateAsync: getExifData, isLoading: _isUploadLoading } =
    useGetProjectImagesExifData("projects", id);
  const { mutateAsync: uploadProjectImages, isLoading: isUploadLoading } =
    useUploadProjectImages("projects", id);
  const theme = useTheme();
  const [isOpenAddModal, setIsOpenAddModal] = useState<boolean>(false);
  const [imagesWithExifData, setImagesWithExifData] = useState<any>([]);
  const [applySettingsToAll, setApplySettingsToAll] = useState(true);
  const [lat, setLat] = useState<any>(null);
  const [lng, setLng] = useState<any>(null);
  const [status, setStatus] = useState<any>("checking");
  const [message, setMessage] = useState<any>("");
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");

  const [cameraModal, setCameraModal] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);

  useEffect(() => {
    if (user) {
      setAuthUser(user);
      setUserRoles(user[`${process.env.REACT_APP_WEB_URL}/roles`]);
    }
  }, [user]);

  const getLocation = async () => {
    if (!navigator.geolocation) {
      setStatus("error");
      setMessage("Your browser does not support geo location.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (
          !position ||
          !position.coords ||
          !position.coords.latitude ||
          !position.coords.longitude
        ) {
          setStatus("error");
          setMessage("No coordinates were returned.");
          const lat = position.coords.latitude
            ? position.coords.latitude
            : null;
          const long = position.coords.longitude
            ? position.coords.longitude
            : null;
          return;
        }

        setLat(position.coords.latitude);
        setLng(position.coords.longitude);
        setStatus("success");
        setMessage(`Coordinates found`);
      },
      (error) => {
        setStatus("error");
        setMessage(`Error getting location. ${error.message}`);
        console.log(`Error occurred trying to get location. ${error.message}`);
      },
      { enableHighAccuracy: false, timeout: 30000, maximumAge: 300000 }
    );
  };

  useEffect(() => {
    // getLocation();
  }, []);

  const handleClose = () => {
    setIsOpenAddModal(false);
    setSelectedFiles([]);
    setImagesWithExifData([]);
    setLat(null);
    setLng(null);
    setLocation("");
    setTitle("");
    setTags([]);
  };

  const handleFileUploadPreview = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event?.target?.files;  
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      const acceptedTypes = types;
      const isValidFileType = fileArray?.every(file => acceptedTypes?.includes(file?.type))  
      if (!isValidFileType) {
        alert('Please select only images (jpeg, png) or videos (mp4, mpeg).')
        return
      }  
      setSelectedFiles(prevFiles => [...prevFiles, ...fileArray]);
      const previews = fileArray?.map(file => URL.createObjectURL(file));
      setImagePreviews(prevPreviews => [...prevPreviews, ...previews]);
      setFileUploaded(true);
    }
  };

  const removeFile = (fileIndex: number) => {
    const newSelectedFiles = [...selectedFiles];
    const newImagePreviews = [...imagePreviews];
    newSelectedFiles.splice(fileIndex, 1);
    newImagePreviews.splice(fileIndex, 1);
    URL.revokeObjectURL(imagePreviews[fileIndex]); // Revoke the blob URL to avoid memory leaks
    setSelectedFiles(newSelectedFiles);
    setImagePreviews(newImagePreviews);
    if (newSelectedFiles.length === 0) {
      setFileUploaded(false);
    }

    setImagesWithExifData((prev: any) =>
      prev.filter((item: any, index: number) => index != fileIndex)
    );
  };

  const convertDMSToDecimal = (degrees: any, minutes: any, seconds: any) => {
    return degrees + minutes / 60 + seconds / 3600;
  };

  const fetchLocationName = async (latitude: any, longitude: any) => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`;

    try {
      const response = await fetch(url);
      const data = await response.json();
      if (data.status === "OK") {
        const locationName = data?.results[0]?.formatted_address;
        setLocation(locationName);
      } else {
        console.error("Geocoding failed:", data.status);
      }
    } catch (error) {
      console.error("Failed to fetch location name:", error);
    }
  };

  const handleUpload = async () => {
    setIsLoading(true);
    const formData = new FormData();
    if (id) {
      formData.append("projectId", id);
    }

    if (userData?._id) {
      formData.append("userId", userData?._id);
    }

    formData.append("type", "project-files");
    selectedFiles.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const data = await getExifData(formData);
      const item = data?.data && data?.data?.length > 0 ? data?.data[0] : {};

      if (
        item &&
        item?.exif?.gps &&
        item?.exif?.gps?.GPSLatitude &&
        item?.exif?.gps?.GPSLongitude
      ) {
        console.log("====< i am in");
        const { GPSLatitude, GPSLongitude, GPSLatitudeRef, GPSLongitudeRef } =
          item?.exif?.gps;

        const latitude = convertDMSToDecimal(
          GPSLatitude[0],
          GPSLatitude[1],
          GPSLatitude[2]
        );
        const longitude = convertDMSToDecimal(
          GPSLongitude[0],
          GPSLongitude[1],
          GPSLongitude[2]
        );

        const finalLat = GPSLatitudeRef === "N" ? latitude : -latitude;
        const finalLng = GPSLongitudeRef === "E" ? longitude : -longitude;

        setLat(finalLat);
        setLng(finalLng);
        fetchLocationName(finalLat, finalLng);
      }
      const name = selectedFiles[0]?.name.split(".");
      name.pop();
      setTitle(name.join(".") ?? "");
      setImagesWithExifData(data.data);
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadImages = async () => {
    setIsLoading(true);
    const formData = new FormData();

    formData.append("notes", tags.join(","));
    formData.append(
      "exifData",
      JSON.stringify({
        location: location,
        position: {
          lat: lat,
          lng: lng,
        },
      })
    );
    if (id) {
      formData.append("projectId", id);
    }

    if (userData?._id) {
      formData.append("userId", userData?._id);
    }

    formData.append("type", "project-files");

    selectedFiles.forEach((file) => {
      try {
        var newFileName =  file.name;
        var newFile = new File([file], newFileName, { type: file.type });
        formData.append("files", newFile);
      } catch (err) {
        console.log("🚀 ~ selectedFiles.forEach ~ err:", err);
      }
    });

    try {
      await uploadProjectImages(formData);

      setIsLoading(false);
      setFileUploaded(true);
      setSelectedFiles([]);
      setImagePreviews([]);
      setImagesWithExifData([]);
    } catch (error) {
      console.error("Error uploading files:", error);
      setIsLoading(false);
    } finally {
      handleClose();
    }
  };

  const onAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setLocation(event.target.value);
    // handlePlaceChanged(index);
  };

  const onImageCaptureHandler = (image: any) => {
    setCapturedImage(image);
  };

  return (
    <>
      <Button
        variant="contained"
        sx={{
          padding: theme.spacing(1.5, 3, 1.5, 3),
          borderRadius: "8px",
          textTransform: "capitalize",
          width: 140,
          fontSize: "13px",
          marginLeft: 2,
          whiteSpace: "nowrap",
        }}
        onClick={() => {
          setIsOpenAddModal(true);
          setCameraModal(false);
        }}
      >
        <CameraIcon /> <Box sx={{ width: 10 }} /> Add Photo
      </Button>
      <Dialog open={isOpenAddModal} onClose={handleClose}
        PaperProps={{
          style: {
            border: "1px solid #CCCCCC",
            borderRadius: "22px",
            padding: cameraModal ? 2 : 3,
            maxHeight: "95vh",
            boxShadow: "24px",
            width: "100%",
            maxWidth: imagesWithExifData.length == 1 ? 850 : 350,
          },
        }} >
        <DialogContent>
          <Box>
            {isLoading && (
              <Box
                sx={{
                  backgroundColor: "rgba(0,0,0,.3)",
                  borderRadius: "22px",
                  width: "100%",
                  height: "100vh",
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 9999,
                }}
              >
                <CircularProgress
                  size={30}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    color: "white",
                  }}
                />
              </Box>
            )}
            {imagesWithExifData.length > 0 ? (
              <Box sx={{}}>
                <IconButton
                  sx={{ position: "absolute", top: 8, right: 8 }}
                  onClick={() => handleClose()}
                >
                  <Close />
                </IconButton>
                <br />

                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent={"flex-start"}
                  flexDirection={"row"}
                  overflow={"scroll"}
                  paddingBottom={2}
                  sx={{
                    "::-webkit-scrollbar": {
                      display: "none",
                    },
                  }}
                >
                  {selectedFiles.map((file, index) => (
                    console.log(">>>>>>>>>>>>>>>>>>>>>>>>>",file),
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mr: 2,
                        width: 60,
                        height: 90,
                        position: "relative",
                      }}
                    >
                        {['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(file.type) ? (
                        <CardMedia
                        component="img"
                        sx={{
                          width: 60,
                          height: 90,
                          objectFit: "cover",
                          backgroundColor: "#E6E4EA",
                          borderRadius: "8px",
                        }}
                        image={imagePreviews[index]}
                        alt={file.name}
                      />):(
                         // @ts-ignore
                        <CardMedia
                         // @ts-ignore
                        component="video"
                        sx={{
                          width: 60,
                          height: 90,
                          objectFit: "cover",
                          backgroundColor: "#E6E4EA",
                          borderRadius: "8px",
                        }}
                        image={imagePreviews[index]}
                        alt={file.name}
                      />
                      )}

                      <IconButton
                        onClick={() => removeFile(index)}
                        sx={{
                          position: "absolute",
                          bottom: -15,
                          right: -15,
                          backgroundColor: "transparent",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ marginTop: 3 }}>
                  <Typography
                    sx={{
                      color: "#2B262C",
                      fontWeight: "500",
                      fontFamily: "Rza",
                      fontSize: 20,
                      textUnderlineOffset: 7,
                      textDecoration: "underline",
                      textDecorationColor: "#F0845D",
                    }}
                  >
                    Details
                  </Typography>

                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 3,
                    }}
                  >
                    {selectedFiles.length == 1 && (
                      <Box sx={{ width: "50%" }}>
                        <Box
                          alignItems={"flex-start"}
                          justifyContent={"center"}
                          display={"flex"}
                          sx={{
                            padding: "10px 2px",
                            borderBottom: "1px solid #D7D4DE",
                          }}
                        >
                          <Typography
                            sx={{ color: "#7C7A80", width: "100%", fontSize: 15 }}
                          >
                            Taken by
                          </Typography>
                          <Box style={{ width: "100%" }}>
                            <Typography
                              sx={{
                                color: "#75788F",
                                width: "100%",
                                textAlign: "right",
                                fontSize: 15,
                              }}
                            >
                              {"---"}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          alignItems={"flex-start"}
                          justifyContent={"center"}
                          display={"flex"}
                          sx={{
                            padding: "10px 2px",
                            borderBottom: "1px solid #D7D4DE",
                          }}
                        >
                          <Typography
                            sx={{ color: "#7C7A80", width: "100%", fontSize: 15 }}
                          >
                            Taken on
                          </Typography>
                          <Box style={{ width: "100%" }}>
                            <Typography
                              sx={{
                                fontSize: 15,
                                color: "#75788F",
                                width: "100%",
                                textAlign: "right",
                              }}
                            >
                              {imagesWithExifData[0]?.exif?.exif?.CreateDate
                                ? formatDateTime(
                                  moment(
                                    imagesWithExifData[0]?.exif?.exif
                                      ?.CreateDate,
                                    "YYYY:MM:DD HH:mm:ss"
                                  )
                                    .toDate()
                                    .toString()
                                )
                                : "----"}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          alignItems={"flex-start"}
                          justifyContent={"center"}
                          display={"flex"}
                          sx={{
                            padding: "10px 2px",
                            borderBottom: "1px solid #D7D4DE",
                          }}
                        >
                          <Typography
                            sx={{ color: "#7C7A80", width: "100%", fontSize: 15 }}
                          >
                            Added on
                          </Typography>
                          <Box style={{ width: "100%" }}>
                            <Typography
                              sx={{
                                fontSize: 15,
                                color: "#75788F",
                                width: "100%",
                                textAlign: "right",
                              }}
                            >
                              {formatDateTime(new Date().toString())}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#5A525B",
                            width: "100%",
                            fontWeight: "600",
                            marginTop: 4,
                          }}
                        >
                          Apply the settings to all the images you’ve uploaded.
                        </Typography>
                        <RadioGroup
                          aria-labelledby={`automaticClockOut_label`}
                          defaultValue=""
                          value={applySettingsToAll}
                          name={"automaticClockOutEnabled"}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            flexDirection: "row",
                            padding: theme.spacing(0.85, 1, 0.85, 0),
                          }}
                          onChange={(e) => {
                            // @ts-ignore
                            setApplySettingsToAll(e.target.value);
                          }}
                        >
                          <FormControlLabel
                            value={true}
                            control={<Radio />}
                            label={"Yes"}
                          />
                          <FormControlLabel
                            value={false}
                            control={<Radio />}
                            label={"No"}
                          />
                        </RadioGroup>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#5A525B",
                            width: "100%",
                            fontWeight: "600",
                            marginTop: 2,
                          }}
                        >
                          GPS Location
                        </Typography>
                        <GoogleMap
                          key={`1_map`}
                          id={`1_map`}
                          mapContainerStyle={{
                            width: "100%",
                            height: "200px",
                            marginTop: 10,
                            borderRadius: "10px",
                          }}
                          center={{
                            lat: lat,
                            lng: lng,
                          }}
                          zoom={15}
                          options={{
                            clickableIcons: false,
                            disableDefaultUI: true,
                          }}
                        >
                          <Fragment>
                            <MarkerF
                              position={{
                                lat: lat,
                                lng: lng,
                              }}
                              label={""}
                            ></MarkerF>
                          </Fragment>
                        </GoogleMap>
                      </Box>
                    )}
                    <Box
                      sx={{
                        width: imagesWithExifData.length == 1 ? "50%" : "100%",
                        height: "100%",
                      }}
                    >
                      <Box width="100%">
                        {selectedFiles.length == 1 && (
                          <>
                            <Typography
                              fontSize={"14px"}
                              color="#5A525B"
                              paddingBottom={0.5}
                              fontWeight="bold"
                            >
                              Title
                            </Typography>
                            <Box
                              sx={{
                                backgroundColor: "white",
                                borderRadius: "8px",
                                border: "1px solid #DBDBDB",
                                padding: theme.spacing(0.8, 1.6, 0.8, 1.6),
                              }}
                              display={"flex"}
                              alignItems={"center"}
                              justifyContent={"center"}
                            >
                              <Input
                                disableUnderline
                                sx={{
                                  width: "100%",
                                  fontSize: "17px",
                                  "& input::placeholder": {
                                    fontWeight: "bold",
                                  },
                                }}
                                type="text"
                                fullWidth={true}
                                onChange={(e: any) => setTitle(e.target.value)}
                                value={title}
                                required={true}
                                disabled={false}
                              />
                            </Box>
                          </>
                        )}
                        {selectedFiles.length == 1 && (
                          <Box width="100%" sx={{ marginTop: 2 }}>
                            <Typography
                              fontSize={"14px"}
                              color="#5A525B"
                              paddingBottom={0.5}
                              fontWeight="bold"
                            >
                              Location
                            </Typography>
                            <StandaloneSearchBox
                              onLoad={(ref: any) =>
                                (locationFieldRef.current = ref)
                              }
                              onPlacesChanged={() => {
                                const place =
                                  locationFieldRef.current.getPlaces()[0];
                                if (place) {
                                  setLocation(place?.formatted_address);
                                  setLat(place?.geometry?.location.lat());
                                  setLng(place?.geometry?.location.lng());
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  backgroundColor: "white",
                                  borderRadius: "8px",
                                  border: "1px solid #DBDBDB",
                                  width: "100%",
                                  padding: theme.spacing(0.8, 1.6, 0.8, 1.6),
                                }}
                                display={"flex"}
                                alignItems={"center"}
                                justifyContent={"center"}
                              >
                                <Input
                                  disableUnderline
                                  sx={{
                                    width: "100%",
                                    fontSize: "17px",
                                  }}
                                  placeholder={""}
                                  key={`0_searchbox`}
                                  id={`0_location`}
                                  name={`0_location`}
                                  type="text"
                                  fullWidth={true}
                                  value={location}
                                  onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                  ) => onAddressChange(e)}
                                  required={true}
                                />
                              </Box>
                            </StandaloneSearchBox>
                          </Box>
                        )}
                        <Box width="100%" sx={{ marginTop: 2 }}>
                          <Typography
                            fontSize={"14px"}
                            color="#5A525B"
                            paddingBottom={0.5}
                            fontWeight="bold"
                          >
                            Tags
                          </Typography>
                          <SelectTagsComponent
                            isFullWidth={selectedFiles.length > 1}
                            tags={tags}
                            setTags={setTags}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <br />
                <br />
                <Box
                  display={"flex"}
                  alignItems={"flex-end"}
                  justifyContent={"flex-end"}
                >
                  <Button
                    variant="contained"
                    sx={{
                      padding: theme.spacing(1.5, 3, 1.5, 3),
                      borderRadius: "8px",
                      textTransform: "capitalize",
                      fontSize: "13px",
                      whiteSpace: "nowrap",
                      color: "black !important",
                      boxShadow: "none",
                      backgroundColor: "#E6E4EA",
                      "&:hover": {
                        boxShadow: "none",
                        backgroundColor: "#E6E4EA",
                      },
                    }}
                    onClick={() => {
                      setImagesWithExifData([]);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      padding: theme.spacing(1.5, 3, 1.5, 3),
                      borderRadius: "8px",
                      textTransform: "capitalize",
                      fontSize: "13px",
                      whiteSpace: "nowrap",
                      marginLeft: 2,
                      color:
                        selectedFiles.length == 0
                          ? "#6B646C !important"
                          : "white !important",
                      boxShadow: "none",
                      "&:hover": {
                        boxShadow: "none",
                      },
                    }}
                    disabled={selectedFiles.length == 0}
                    onClick={() => {
                      handleUploadImages();
                    }}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            ) : cameraModal ? (
              <>
                <WebcamComponent
                  onImageCapture={onImageCaptureHandler}
                  fromProjectPhotos
                  isEnabled={true}
                  onReTake={() => setCapturedImage(null)}
                />
                <Button
                  variant="contained"
                  sx={{
                    padding: theme.spacing(1.5, 3, 1.5, 3),
                    borderRadius: "8px",
                    textTransform: "capitalize",
                    width: "100%",
                    fontSize: "13px",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    color: "white",
                    marginTop: 1,
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                    },
                  }}
                  disabled={capturedImage === null ? true : false}
                  onClick={() => {
                    handleFileUploadPreview({
                      target: {
                         // @ts-ignore
                        files: [capturedImage],
                         // @ts-ignore
                      },
                    });
                    setCameraModal(false);
                  }}
                >
                  <Box sx={{ width: 10 }} /> Use photo
                </Button>
              </>
            ) : (
              <Box sx={{}}>
                <Box
                  display="flex"
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  marginBottom="12px"
                >
                  <Typography
                    sx={{ fontSize: 20, fontWeight: "600", fontFamily: "Rza" }}
                  >
                    Upload Photos and Videos
                  </Typography>
                  <IconButton onClick={() => handleClose()}>
                    <Close />
                  </IconButton>
                </Box>
                <InputLabel htmlFor="file-upload">
                  <input
                    id="file-upload"
                    type="file"
                    onChange={handleFileUploadPreview}
                    multiple
                    accept="image/*, video/*"
                    style={{ display: "none" }}
                  />
                </InputLabel>

                <Box
                  sx={{
                    border: "1px dashed #6B646CAC",
                    borderRadius: "8px",
                    padding: "4rem 1rem",
                    textAlign: "center",
                    height: 294,
                    alignItems: "center",
                    justifyContent: "center",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    fontSize="14px"
                    paddingLeft={2.5}
                    paddingRight={2.5}
                    fontWeight="500"
                    color="#5A525B"
                    fontFamily={"Buenos Aires"}
                  >
                    Click select to upload or drag and drop your files
                  </Typography>
                  {/* {!fileUploaded || selectedFiles.length <= 0 ? ( */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "15px",
                    }}
                  >
                    <IconButton
                      sx={{
                        background: "#F0845D",
                        "&:hover": {
                          background: "#F0845D",
                        },
                        borderRadius: 1.8,
                        padding: "6px 10px",
                      }}
                      disabled={isLoading}
                      onClick={() => {
                        let input = document.getElementById("file-upload");
                        input?.click();
                      }}
                    >
                      <UploadIcon />{" "}
                      <p
                        style={{
                          fontSize: 12,
                          paddingTop: 0,
                          paddingRight: 0,
                          paddingBottom: 0,
                          margin: 0,
                          color: "white",
                          paddingLeft: 7,
                        }}
                      >
                        Select File
                      </p>
                    </IconButton>

                    <IconButton
                      sx={{
                        background: "#E6E4EA",
                        "&:hover": {
                          background: "#E6E4EA",
                        },
                        borderRadius: 1.8,
                        padding: "6px 10px",
                        marginLeft: 1,
                      }}
                      disabled={isLoading}
                      onClick={() => setCameraModal(true)}
                    >
                      <CameraIconGrey />{" "}
                      <p
                        style={{
                          fontSize: 12,
                          paddingTop: 0,
                          paddingRight: 0,
                          paddingBottom: 0,
                          margin: 0,
                          paddingLeft: 7,
                        }}
                      >
                        Open Camera
                      </p>
                    </IconButton>
                  </Box>
                  {/* ) : null} */}
                </Box>

                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent={"flex-start"}
                  flexDirection={"row"}
                  overflow={"scroll"}
                  paddingTop={2}
                  paddingBottom={2}
                  sx={{
                    "::-webkit-scrollbar": {
                      display: "none",
                    },
                  }}
                >
                  {selectedFiles.map((file, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mr: 2,
                        width: 60,
                        height: 90,
                        position: "relative",
                      }}
                    >
                     <CardMedia
                        component="img"
                        sx={{
                          width: 60,
                          height: 90,
                          objectFit: "cover",
                          backgroundColor: "#E6E4EA",
                          borderRadius: "8px",
                        }}
                        image={imagePreviews[index]}
                        alt={file.name}
                    />
                      {isLoading && (
                        <CircularProgress
                          size={24}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                          }}
                        />
                      )}
                      {!isLoading && (
                        <IconButton
                          onClick={() => removeFile(index)}
                          sx={{
                            position: "absolute",
                            bottom: -15,
                            right: -15,
                            backgroundColor: "transparent",
                            "&:hover": {
                              backgroundColor: "transparent",
                            },
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>

                <Button
                  variant="contained"
                  sx={{
                    padding: theme.spacing(1.5, 3, 1.5, 3),
                    borderRadius: "8px",
                    textTransform: "capitalize",
                    width: "100%",
                    fontSize: "13px",
                    whiteSpace: "nowrap",
                    color:
                      selectedFiles.length == 0
                        ? "#6B646C !important"
                        : "white !important",
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                    },
                  }}
                  disabled={selectedFiles.length == 0}
                  onClick={() => {
                    handleUpload();
                  }}
                >
                  {selectedFiles.length == 0 ? (
                    <CameraIconGrey />
                  ) : (
                    <CameraIcon />
                  )}
                  <Box sx={{ width: 10 }} /> Upload
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
